import { FormEvent, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { einladungApi } from '../../services/api';
import { AuthLayout, Fehlermeldung } from './AuthLayout';

/**
 * Annahme einer Einladung.
 *
 * Ohne Anmeldung erreichbar -- der Eingeladene hat noch kein Konto. Der
 * Token im Link bindet den Vorgang; er ist 256 Bit lang und gehört zu
 * genau einer Einladung.
 */
export default function EinladungPage() {
  const { token = '' } = useParams();
  const navigate = useNavigate();
  const [passwort, setPasswort] = useState('');
  const [wiederholung, setWiederholung] = useState('');
  const [fehler, setFehler] = useState<unknown>(null);
  const [laeuft, setLaeuft] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['einladung', token],
    queryFn: () => einladungApi.pruefen(token),
    // Ein ungültiger Token wird nicht besser, wenn man es noch dreimal
    // versucht -- und jeder Versuch sähe für den Benutzer nach Warten aus.
    retry: false,
  });

  async function absenden(e: FormEvent) {
    e.preventDefault();
    if (passwort !== wiederholung) {
      setFehler(new Error('Die beiden Passwörter stimmen nicht überein.'));
      return;
    }
    setFehler(null);
    setLaeuft(true);
    try {
      await einladungApi.annehmen({ token, passwort });
      navigate('/login?eingeladen=1', { replace: true });
    } catch (err) {
      setFehler(err);
    } finally {
      setLaeuft(false);
    }
  }

  if (isLoading) {
    return (
      <AuthLayout titel="Einladung wird geprüft">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Loader2 className="h-4 w-4 animate-spin" />
          Einen Moment bitte…
        </div>
      </AuthLayout>
    );
  }

  if (error || !data) {
    const meldung =
      (error as any)?.response?.data?.error ??
      'Dieser Einladungslink ist nicht mehr gültig.';
    return (
      <AuthLayout titel="Einladung nicht gültig">
        <p className="text-sm text-gray-700">{meldung}</p>
        <p className="mt-3 text-sm text-gray-600">
          Bitten Sie die Person, die Sie eingeladen hat, um eine neue Einladung.
        </p>
        <Link
          to="/login"
          className="mt-6 inline-block text-sm text-primary-600 hover:underline"
        >
          Zur Anmeldung
        </Link>
      </AuthLayout>
    );
  }

  const name = [data.vorname, data.nachname].filter(Boolean).join(' ');

  return (
    <AuthLayout
      titel="Willkommen"
      untertitel={
        <>
          <strong>{data.firma_name}</strong> hat Sie eingeladen. Vergeben Sie ein
          Passwort, dann ist Ihr Zugang eingerichtet.
        </>
      }
      fusszeile={
        <Link to="/login" className="text-primary-600 hover:underline">
          Ich habe bereits ein Konto
        </Link>
      }
    >
      <Fehlermeldung fehler={fehler} />

      <form onSubmit={absenden} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">Name</label>
          <input
            disabled
            value={name}
            className="mt-1 w-full rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">E-Mail</label>
          {/* Nicht änderbar: die Einladung gilt genau dieser Adresse. */}
          <input
            disabled
            value={data.email}
            className="mt-1 w-full rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Passwort</label>
          <input
            type="password"
            required
            minLength={12}
            autoFocus
            autoComplete="new-password"
            value={passwort}
            onChange={(e) => setPasswort(e.target.value)}
            className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
          <p className="mt-1 text-xs text-gray-500">
            Mindestens 12&nbsp;Zeichen. Mehrere Wörter hintereinander sind sicherer
            und leichter zu behalten als ein kurzes Passwort mit Sonderzeichen.
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Passwort wiederholen
          </label>
          <input
            type="password"
            required
            autoComplete="new-password"
            value={wiederholung}
            onChange={(e) => setWiederholung(e.target.value)}
            className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
        </div>

        <button
          type="submit"
          disabled={laeuft}
          className="flex w-full items-center justify-center rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-60"
        >
          {laeuft && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Zugang einrichten
        </button>

        <p className="text-center text-xs text-gray-500">
          Bei der ersten Anmeldung erhalten Sie zusätzlich einen Bestätigungscode
          per E-Mail.
        </p>
      </form>
    </AuthLayout>
  );
}
